import {
  githubAiAccessTokenPrefix,
  githubAiConnectorName,
  githubAppPrivateKeySecretId,
} from "@p0security/integrations-github-ai-shared/secret-manager";
import type { GithubAiComponents } from "@p0security/integrations-github-ai-shared/types";
import type { FrontendInstallContext } from "@p0security/integrations-installer";
import {
  type SecretManagerLocator,
  awsConnectorTf,
  gcpConnectorTf,
  p0InvokerEmail,
  p0InvokerRoleName,
  secretProviderFor,
  shellLines,
  tfId,
} from "@p0security/integrations-vaulted-credential-shared";
import type { ItemConfigOf } from "@p0security/shared/install/types";

type SecretManagerConfig = ItemConfigOf<
  GithubAiComponents["credential"]
>["secretManager"];

export type InstallContext = FrontendInstallContext<unknown>;

const GCP_CONNECTOR_IMAGE = {
  imageName: "docker.io/p0security/p0-connector-github-ai-gcloud",
  digest:
    "sha-c75e58d@sha256:89921ca9c7d3d15503ec351a7577a18a0760ab9b9e3594ed6725b244075dc75d",
} as const;

const privateKeySecretId = (
  provider: SecretManagerLocator["provider"],
  organization: string
) =>
  secretProviderFor(provider).replacer(
    githubAppPrivateKeySecretId(organization)
  );

const gcpPrivateKeySecretId = (organization: string) =>
  privateKeySecretId("gcp", organization);

const AWS_CONNECTOR_IMAGE = {
  imageName: "p0security/p0-connector-github-ai-aws",
  digest:
    "sha-51ed50d@sha256:606ec0370f7a7bae9c3dfb44b2f552898e1212cdd182648b56e96ffcdbf0d1bf",
} as const;

export const awsTf = (
  context: InstallContext,
  itemId: string,
  secretManager: Extract<SecretManagerConfig, { type: "aws-sm" }>
) =>
  awsConnectorTf({
    names: { resource: "github", label: "GitHub" },
    image: AWS_CONNECTOR_IMAGE,
    accountId: secretManager.install,
    region: secretManager.connectorRegion,
    secretsRegion: secretManager.secretsRegion,
    connectorName: githubAiConnectorName,
    p0RoleName: p0InvokerRoleName(context),
    tokenSecretPrefix: secretProviderFor("aws").replacer(
      githubAiAccessTokenPrefix(itemId)
    ),
    installSecret: {
      resource: "private_key",
      itemTfId: tfId(itemId),
      secretId: privateKeySecretId("aws", itemId),
      label: "private key",
    },
  });

export const awsStorePrivateKey = (
  organization: string,
  secretManager: Extract<SecretManagerConfig, { type: "aws-sm" }>
) =>
  shellLines(
    "aws secretsmanager put-secret-value",
    `--secret-id ${privateKeySecretId("aws", organization)}`,
    `--region ${secretManager.secretsRegion}`,
    "--secret-string file://private-key.pem"
  );

export const gcpStorePrivateKey = (
  organization: string,
  secretManager: Extract<SecretManagerConfig, { type: "gcp-sm" }>
) =>
  shellLines(
    `gcloud secrets versions add ${gcpPrivateKeySecretId(organization)}`,
    `--project=${secretManager.install}`,
    "--data-file=private-key.pem"
  );

export const gcpTf = (
  context: InstallContext,
  itemId: string,
  secretManager: Extract<SecretManagerConfig, { type: "gcp-sm" }>
) =>
  gcpConnectorTf({
    names: { resource: "github", role: "Github", label: "GitHub" },
    image: GCP_CONNECTOR_IMAGE,
    projectId: secretManager.install,
    region: secretManager.connectorRegion,
    connectorName: secretManager.connectorServiceName,
    serviceAccountId:
      secretManager.connectorServiceAccount?.split("@")[0] ?? "",
    invokerEmail: p0InvokerEmail(context.installed),
    tokenSecretPrefix: secretProviderFor("gcp").replacer(
      githubAiAccessTokenPrefix(itemId)
    ),
    installSecret: {
      resource: "private_key",
      itemTfId: tfId(itemId),
      secretId: gcpPrivateKeySecretId(itemId),
      label: "private key",
    },
  });
